<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  fetchCatalog, buyFromCatalog, quickSellArtifact,
  fetchSellQuote, marketSellArtifact, fetchPending,
  type MarketCurrency, type CatalogItem, type SellQuote, type PendingSaleEntry,
} from '../game/market-api';
import {
  fetchShop, buyShopItem,
  type ShopItem, type ShopGroup, type ShopState, type SlotTerms,
} from '../game/shop-api';
import {
  fetchInventory, SLOTS,
  type Inventory as GearInventory, type Artifact, type ArtifactRarity, type ArtifactType, type EquipSlot,
} from '../game/inventory-api';
import { RARITY_HEX, SLOT_LABELS, scaledStats, STAT_META } from '../game/item-math';
import { BG, itemThumb, shopArt } from '../game/art';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
import ItemModal from './ItemModal.vue';
import ChestReveal from './ChestReveal.vue';
import SwipeDeck from './SwipeDeck.vue';
import cashIcon from '../assets/omerta/icons/cash.webp';
import goldBarsIcon from '../assets/omerta/icons/gold-bars.webp';

/**
 * "THE QUARTER" — the flagship commerce screen. NPC storefront (The Rack),
 * consumables/boosts (Supplies & Favors), and instant quick-sell (Sell),
 * plus the Counting House (gold bars → cash exchange). One data fetch
 * (refreshAll) feeds every tab so switching tabs never re-hits the network.
 */
type Tab = 'rack' | 'supplies' | 'sell';
const props = defineProps<{ initialTab?: Tab; spotlightItem?: string | null }>();
const emit = defineEmits<{ (e: 'updated'): void; (e: 'topup'): void; (e: 'counting'): void }>();

const tab = ref<Tab>(props.initialTab ?? 'rack');

const loading = ref(true);
const error = ref('');
const notice = ref('');
const busy = ref<string | null>(null);

// ── Shared data (one fetch feeds every tab) ─────────────────────────────
const inventory = ref<GearInventory | null>(null);
const npcCatalog = ref<CatalogItem[]>([]);
const shopCatalog = ref<ShopItem[]>([]);
const balance = ref<{ soft: number; premium: number }>({ soft: 0, premium: 0 });
const shopState = ref<ShopState | null>(null);
const slots = ref<SlotTerms | null>(null);
const slotsMaxed = computed(() => slots.value != null && slots.value.nextPrice == null);

const bag = computed<Artifact[]>(() => inventory.value?.bag ?? []);

async function refreshAll() {
  error.value = '';
  try {
    const [inv, shop, rack] = await Promise.all([
      fetchInventory(), fetchShop(), fetchCatalog(),
    ]);
    inventory.value = inv;
    shopCatalog.value = shop.catalog;
    balance.value = shop.balance;
    shopState.value = shop.state;
    slots.value = shop.slots;
    npcCatalog.value = rack;
  } catch (e) {
    error.value = (e as Error).message;
  }
}

onMounted(async () => {
  await refreshAll();
  loading.value = false;
  await loadPending();
  pendingTimer = window.setInterval(() => { nowTick.value = Date.now(); }, 30_000);
});
onUnmounted(() => { if (pendingTimer) window.clearInterval(pendingTimer); });

function slotOf(type: ArtifactType): EquipSlot {
  return type.toLowerCase() as EquipSlot;
}

// ── THE RACK — NPC catalog (cash) + Vault (gold bars) unified in one grid ──
interface RackCard {
  id: string;
  source: 'catalog' | 'vault';
  name: string;
  rarity: ArtifactRarity;
  type: ArtifactType;
  attack: number; defense: number; magic: number; speed: number; hp: number;
  level: number;
  setName: string | null;
  statusEffect: string | null;
  description: string | null;
  price: number;
  currency: MarketCurrency;
  /** Second price in gold bars, when the piece has a premium SKU. */
  altBars?: number | null;
}

function catalogToCard(c: CatalogItem): RackCard {
  return {
    id: c.id, source: 'catalog', name: c.name,
    rarity: c.rarity as ArtifactRarity, type: c.type as ArtifactType,
    attack: c.attack, defense: c.defense, magic: c.magic, speed: c.speed, hp: c.hp,
    level: 1, setName: c.setName, statusEffect: c.statusEffect, description: null,
    // The asking price is shopPrice/priceBars, never basePrice — basePrice is
    // the salvage anchor. Every rarity above COMMON carries both, and bars are
    // always the better value, so the player is offered the choice.
    price: c.shopPrice,
    currency: 'SOFT',
    altBars: c.priceBars,
  };
}
function vaultToCard(i: ShopItem): RackCard {
  const v = i.vault!; // vaultItems filter guarantees the spec is present
  return {
    id: i.id, source: 'vault', name: v.name ?? i.name,
    rarity: v.rarity, type: v.type,
    attack: v.attack, defense: v.defense, magic: v.magic, speed: v.speed, hp: v.hp,
    level: v.level, setName: v.setName ?? null, statusEffect: v.statusEffect ?? null, description: i.description,
    price: i.price, currency: i.currency,
  };
}

/** Only REAL gear belongs in the Rack — the Golden Chest (gacha) and Stash
 *  Expansion share the 'vault' group but have their own cards in Supplies. */
const vaultItems = computed(() =>
  shopCatalog.value.filter((i) => i.kind === 'vault_artifact' && i.vault));
const rackCards = computed<RackCard[]>(() => [
  ...npcCatalog.value.map(catalogToCard),
  ...vaultItems.value.map(vaultToCard),
]);

const RACK_SLOTS: (EquipSlot | 'ALL')[] = ['ALL', ...SLOTS];
const RACK_RARITIES: (ArtifactRarity | 'ALL')[] = ['ALL', 'COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY'];
const rackSlot = ref<EquipSlot | 'ALL'>('ALL');
const rackRarity = ref<ArtifactRarity | 'ALL'>('ALL');
const rackInspect = ref<RackCard | null>(null);

const filteredRack = computed(() => rackCards.value.filter((c) =>
  (rackSlot.value === 'ALL' || slotOf(c.type) === rackSlot.value) &&
  (rackRarity.value === 'ALL' || c.rarity === rackRarity.value),
));

function rarityLabel(r: ArtifactRarity): string {
  return r.charAt(0) + r.slice(1).toLowerCase();
}

function rackAfford(c: RackCard): boolean {
  return c.currency === 'PREMIUM' ? balance.value.premium >= c.price : balance.value.soft >= c.price;
}
/** Preemptive block — mirrors the backend's "Stash full" 400 before the player even tries. */
function rackBlocked(_c: RackCard): string | null {
  if (inventory.value && inventory.value.bagCount >= inventory.value.bagCap) return 'Stash full';
  return null;
}
function rackModalBlocked(c: RackCard): string | null {
  return rackBlocked(c) ?? (!rackAfford(c) ? 'Not enough funds' : null);
}
function priceLabel(price: number, currency: MarketCurrency): string {
  return `${price.toLocaleString()} ${currency === 'PREMIUM' ? 'gold bars' : 'cash'}`;
}

function cardToArtifact(c: RackCard): Artifact {
  return {
    id: c.id, name: c.name, description: c.description,
    rarity: c.rarity, type: c.type,
    attack: c.attack, defense: c.defense, magic: c.magic, speed: c.speed, hp: c.hp,
    level: c.level, setName: c.setName, statusEffect: c.statusEffect,
    // catalog cards carry their asking price; the sell-payout preview never
    // renders in buy mode, so mirroring price into both fields is safe
    currentPrice: c.price, basePrice: c.price,
    imageUrl: null, isBreakable: false, maxDurability: null, durability: null, isBroken: false,
  };
}
function equippedFor(c: RackCard): Artifact | null {
  return inventory.value?.equipped[slotOf(c.type)] ?? null;
}

async function onBuyRack(c: RackCard, pay: MarketCurrency = 'SOFT') {
  const premium = pay === 'PREMIUM';
  const cost = premium ? c.altBars! : c.price;
  const ok = await confirmAction({
    kicker: 'The Rack',
    title: `Buy ${c.name}?`,
    icon: itemThumb(c.type, c.rarity, c.name),
    rows: [
      { label: 'Piece', value: `${rarityLabel(c.rarity)} ${SLOT_LABELS[slotOf(c.type)]} · Lv ${c.level}`, tone: 'dim' },
      { label: 'Price', value: cost.toLocaleString(), icon: premium ? goldBarsIcon : cashIcon, tone: premium ? 'bright' : 'gold' },
    ],
    confirmLabel: 'Buy it',
  });
  if (!ok) return;
  busy.value = c.id; error.value = ''; notice.value = '';
  try {
    if (c.source === 'catalog') {
      const res = await buyFromCatalog(c.id, pay);
      toast(`Bought ${res.name} — it's in your bag`, { icon: itemThumb(c.type, c.rarity, c.name), accent: RARITY_HEX[c.rarity] });
    } else {
      const res = await buyShopItem(c.id);
      toast(res.granted ? `Acquired ${res.granted.name} — it's in your bag` : `Bought ${res.name}`, { icon: itemThumb(c.type, c.rarity, c.name), accent: RARITY_HEX[c.rarity] });
    }
    rackInspect.value = null;
    await refreshAll();
    emit('updated');
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

function statLine(a: Artifact): string {
  const parts: string[] = [];
  if (a.attack) parts.push(`FPR ${a.attack}`);
  if (a.defense) parts.push(`TUF ${a.defense}`);
  if (a.magic) parts.push(`CUN ${a.magic}`);
  if (a.speed) parts.push(`RFX ${a.speed}`);
  if (a.hp) parts.push(`HP ${a.hp}`);
  return parts.length ? parts.join(' · ') : 'No bonuses';
}

// ── SUPPLIES & FAVORS — shop consumables / boosts / chest / expansion ───
const GROUPS: { key: ShopGroup; title: string; blurb: string }[] = [
  { key: 'consumable', title: 'Supplies', blurb: 'Pay in gold bars — patch up and steady your nerve.' },
  { key: 'boost', title: 'Favors', blurb: 'Pay in gold bars — leverage that pays off.' },
];
const SPECIAL_KINDS = new Set(['chest_pull', 'inventory_slot']);
function itemsOf(group: ShopGroup): ShopItem[] {
  return shopCatalog.value.filter((i) => i.group === group && !SPECIAL_KINDS.has(i.kind));
}
const chestItem = computed(() => shopCatalog.value.find((i) => i.kind === 'chest_pull') ?? null);
const expansionItem = computed(() => shopCatalog.value.find((i) => i.kind === 'inventory_slot') ?? null);

function durationLabel(ms: number | null): string {
  if (!ms) return '';
  const mins = Math.round(ms / 60000);
  return mins >= 60 ? `${Math.round(mins / 60)}h` : `${mins}m`;
}
function magnitudeLabel(i: ShopItem): string | null {
  if (i.kind === 'hp_partial' && i.magnitude != null) return `+${Math.round(i.magnitude * 100)}% health`;
  return null;
}
function affordable(i: ShopItem): boolean {
  return i.currency === 'PREMIUM' ? balance.value.premium >= i.price : balance.value.soft >= i.price;
}
function blockedReason(i: ShopItem): string | null {
  const s = shopState.value;
  if (!s) return null;
  if (i.kind === 'hp_refill' || i.kind === 'hp_partial') { if (s.hp >= s.maxHp) return 'Full health'; }
  if (i.kind === 'nerve_refill') {
    if (s.nerve >= s.maxNerve) return 'Full nerve';
    if (i.dailyLimit != null && s.nerveRefillsUsedToday >= i.dailyLimit) return 'Daily limit';
  }
  if (i.kind === 'revive' && s.hasRevive) return 'Already held';
  if ((i.kind === 'vault_artifact' || i.kind === 'chest_pull') && s.bagCount >= s.bagCap) return 'Stash full';
  return null;
}
function activeHint(i: ShopItem): string | null {
  const s = shopState.value;
  if (!s) return null;
  if (i.kind === 'xp_booster' && s.xpBoosterUntil) return 'Active';
  if (i.kind === 'cash_rush' && s.cashRushUntil) return 'Active';
  return null;
}
function dailyCapLabel(i: ShopItem): string | null {
  const s = shopState.value;
  if (!s || i.kind !== 'nerve_refill' || i.dailyLimit == null) return null;
  return `×${s.nerveRefillsUsedToday}/${i.dailyLimit} today`;
}

async function onBuySupply(i: ShopItem) {
  const chest = i.kind === 'chest_pull';
  const ok = await confirmAction({
    kicker: chest ? 'The Golden Chest' : 'Supplies & Favors',
    title: chest ? 'Pull from the Golden Chest?' : `Buy ${i.name}?`,
    icon: shopArt(i.kind) || undefined,
    rows: [
      { label: 'Price', value: i.price.toLocaleString(), icon: i.currency === 'PREMIUM' ? goldBarsIcon : cashIcon, tone: i.currency === 'PREMIUM' ? 'bright' : 'gold' },
    ],
    note: i.description ?? undefined,
    confirmLabel: chest ? 'Pull' : 'Buy it',
  });
  if (!ok) return;
  busy.value = i.id; error.value = ''; notice.value = '';
  try {
    const res = await buyShopItem(i.id);
    await refreshAll();
    emit('updated');
    if (chest && res.granted) {
      // The Golden Chest gets a cinematic reveal instead of a plain toast —
      // pull the full stats from the freshly-refreshed bag for the card.
      const full = inventory.value?.bag.find((a) => a.id === res.granted!.id) ?? null;
      chestReward.value = full ?? {
        id: res.granted.id, name: res.granted.name, description: null,
        rarity: res.granted.rarity, type: res.granted.type,
        attack: 0, defense: 0, magic: 0, speed: 0, hp: 0, level: 1,
        setName: null, statusEffect: null, currentPrice: 0, basePrice: 0,
        imageUrl: null, isBreakable: false, maxDurability: null, durability: null, isBroken: false,
      };
    } else {
      toast(res.granted ? `Acquired ${res.granted.name} — it's in your bag` : `Bought ${res.name}`, { icon: shopArt(i.kind) });
    }
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

// The Golden Chest reveal overlay — set to the won piece after a pull.
const chestReward = ref<Artifact | null>(null);

// ── SELL — pick from the bag; quick-sell now (50%) or fence it (90%, 12h lock) ──
const sellTarget = ref<Artifact | null>(null);
const sellQuote = ref<SellQuote | null>(null);
const quoteLoading = ref(false);

async function pickToSell(a: Artifact) {
  sellTarget.value = a;
  sellQuote.value = null;
  quoteLoading.value = true;
  try { sellQuote.value = await fetchSellQuote(a.id); }
  catch (e) { error.value = (e as Error).message; }
  finally { quoteLoading.value = false; }
}

async function onQuicksell() {
  const a = sellTarget.value; const q = sellQuote.value;
  if (!a || !q) return;
  const ok = await confirmAction({
    kicker: 'Quick sell',
    title: `Quick-sell ${a.name}?`,
    icon: itemThumb(a.type, a.rarity, a.name),
    rows: [
      { label: 'Street value', value: q.gross.toLocaleString(), icon: cashIcon, tone: 'dim' },
      { label: `You lose (${100 - q.quick.pct}%)`, value: `−${(q.gross - q.quick.payout).toLocaleString()}`, tone: 'error' },
      { label: 'You get now', value: q.quick.payout.toLocaleString(), icon: cashIcon, tone: 'success' },
    ],
    note: 'Instant payout, half the street value — and sold is sold.',
    confirmLabel: `Take ${q.quick.payout.toLocaleString()}`,
    danger: true,
  });
  if (!ok) return;
  busy.value = 'quicksell'; error.value = '';
  try {
    const res = await quickSellArtifact(a.id);
    toast(`Quick-sold — +${res.value.toLocaleString()} cash`, { icon: cashIcon });
    sellTarget.value = null; sellQuote.value = null;
    await refreshAll(); await loadPending();
    emit('updated');
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

async function onMarketSell() {
  const a = sellTarget.value; const q = sellQuote.value;
  if (!a || !q) return;
  const ok = await confirmAction({
    kicker: 'The fence',
    title: `Fence ${a.name}?`,
    icon: itemThumb(a.type, a.rarity, a.name),
    rows: [
      { label: 'Street value', value: q.gross.toLocaleString(), icon: cashIcon, tone: 'dim' },
      { label: `Fence's cut (${100 - q.market.pct}%)`, value: `−${(q.gross - q.market.payout).toLocaleString()}`, tone: 'error' },
      { label: `Payout in ${q.market.lockHours}h`, value: q.market.payout.toLocaleString(), icon: cashIcon, tone: 'success' },
    ],
    note: `The item goes to the fence now; the cash clears after a ${q.market.lockHours}-hour lock. Sold is sold.`,
    confirmLabel: `Fence for ${q.market.payout.toLocaleString()}`,
  });
  if (!ok) return;
  busy.value = 'marketsell'; error.value = '';
  try {
    const res = await marketSellArtifact(a.id);
    toast(`At the fence — +${res.payout.toLocaleString()} cash in ${q.market.lockHours}h`, { icon: cashIcon });
    sellTarget.value = null; sellQuote.value = null;
    await refreshAll(); await loadPending();
    emit('updated');
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

// ── At the fence — locked payouts; the server credits any that matured on fetch ──
const pending = ref<PendingSaleEntry[]>([]);
const nowTick = ref(Date.now());
let pendingTimer = 0;

async function loadPending() {
  try {
    const v = await fetchPending();
    pending.value = v.pending;
    if (v.collected > 0) {
      toast(`The fence cleared — +${v.collected.toLocaleString()} cash`, { icon: cashIcon });
      await refreshAll();
      emit('updated');
    }
  } catch { /* non-fatal — the fence list just stays as-is */ }
}

/** Time left on a locked payout, e.g. "3h 12m". */
function untilLabel(matureAt: string): string {
  const ms = new Date(matureAt).getTime() - nowTick.value;
  if (ms <= 0) return 'clearing…';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// The Counting House (bars → cash) is its own full screen now — see
// CountingHouse.vue; the wallet-strip button just asks HeroScreen to open it.
</script>

<template>
  <section>
    <div class="relative rounded-lg overflow-hidden mb-5 h-36 corner-frame">
      <img :src="BG.market" alt="" class="w-full h-full object-cover" />
      <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0.25),rgba(10,7,20,0.94))"></div>
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <div class="absolute bottom-3 left-4">
        <h1 class="font-heading text-2xl text-gold">The Quarter</h1>
        <span class="font-label uppercase tracking-wider text-sm text-text-dim block">Buy, sell, launder</span>
      </div>
    </div>

    <!-- WALLET STRIP -->
    <div class="flex flex-wrap items-center gap-2 mb-6">
      <div class="mkt-wallet-chip">
        <img :src="cashIcon" class="w-5 h-5 shrink-0" alt="cash" />
        <span class="font-heading text-gold leading-none">{{ balance.soft.toLocaleString() }}</span>
        <span class="font-label uppercase tracking-wider text-[10px] text-text-dim leading-none">cash</span>
      </div>
      <div class="mkt-wallet-chip mkt-bars-pulse">
        <img :src="goldBarsIcon" class="w-5 h-5 shrink-0" alt="gold bars" />
        <span class="font-heading text-gold-bright leading-none">{{ balance.premium.toLocaleString() }}</span>
        <span class="font-label uppercase tracking-wider text-[10px] text-text-dim leading-none">gold bars</span>
      </div>
      <button class="btn-deco btn-deco--ghost" @click="emit('topup')">+ Buy gold bars</button>
      <div class="flex-1"></div>
      <button class="btn-deco btn-deco--gold max-w-full" @click="emit('counting')">
        <span class="sm:hidden">Counting House</span>
        <span class="hidden sm:inline">Counting House — turn bars into cash</span>
      </button>
    </div>

    <!-- TABS -->
    <nav class="flex flex-wrap gap-1 mb-6 border-b border-border">
      <button v-for="t in (['rack', 'supplies', 'sell'] as const)" :key="t" @click="tab = t"
        :class="['tab-deco', tab === t && 'tab-deco-active']">
        {{ t === 'rack' ? 'The Rack' : t === 'supplies' ? 'Supplies & Favors' : 'Sell' }}
        <span v-if="t === 'rack'" class="mkt-count">{{ rackCards.length }}</span>
        <span v-else-if="t === 'sell'" class="mkt-count">{{ bag.length }}</span>
      </button>
    </nav>

    <p v-if="loading" class="text-text-dim">Loading…</p>
    <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>
    <p v-if="notice" class="mb-4 text-sm text-success">{{ notice }}</p>

    <template v-if="!loading">
      <!-- ═══ THE RACK ═══════════════════════════════════════════════ -->
      <div v-if="tab === 'rack'">
        <div class="flex flex-wrap items-center gap-2 mb-3">
          <span class="font-label uppercase tracking-wider text-[11px] text-text-dim mr-1">Slot</span>
          <button v-for="s in RACK_SLOTS" :key="'rs-' + s" @click="rackSlot = s"
            :class="['chip-deco', rackSlot === s && 'chip-deco-active']">{{ s === 'ALL' ? 'All' : SLOT_LABELS[s] }}</button>
        </div>
        <div class="flex flex-wrap items-center gap-2 mb-5">
          <span class="font-label uppercase tracking-wider text-[11px] text-text-dim mr-1">Rarity</span>
          <button v-for="r in RACK_RARITIES" :key="'rr-' + r" @click="rackRarity = r"
            :class="['chip-deco', rackRarity === r && 'chip-deco-active']">
            <span v-if="r !== 'ALL'" class="mkt-dot" :style="{ background: RARITY_HEX[r] }"></span>
            {{ r === 'ALL' ? 'All' : rarityLabel(r) }}
          </button>
        </div>

        <p v-if="filteredRack.length === 0" class="font-body text-text-dim text-sm">Nothing matches those filters.</p>
        <SwipeDeck
          v-else :items="filteredRack" :page-size="6"
          min-col="150px" gap="0.75rem" :item-key="(c: any) => c.source + c.id"
        >
          <template #default="{ item: c }">
          <div
            class="mkt-tile"
            :class="{ 'mkt-legendary': c.rarity === 'LEGENDARY' }"
            :style="{ '--rar': RARITY_HEX[c.rarity] }"
            role="button" tabindex="0"
            :title="`${c.name} — details`"
            @click="rackInspect = c"
            @keydown.enter="rackInspect = c"
          >
            <span v-if="c.rarity === 'LEGENDARY'" class="mkt-hot">Hot</span>
            <span class="mkt-tile-lv">Lv {{ c.level }}</span>
            <span class="mkt-tile-artwrap">
              <img :src="itemThumb(c.type, c.rarity, c.name)" :alt="c.type" class="mkt-tile-art" width="52" height="52" loading="lazy" />
            </span>
            <span class="mkt-tile-name" :style="{ color: RARITY_HEX[c.rarity] }" :title="c.name">{{ c.name }}</span>
            <span class="mkt-tile-meta">
              {{ SLOT_LABELS[slotOf(c.type)] }}<template v-if="c.setName"> · {{ c.setName }}</template>
              <template v-if="c.source === 'vault'"> · Vault</template>
              <template v-if="c.statusEffect"> · <i class="mkt-tile-fx">{{ c.statusEffect }}</i></template>
            </span>
            <!-- spec plate — same anatomy on every card, values over engraved labels -->
            <span class="mkt-specs">
              <template v-for="(v, k) in scaledStats(cardToArtifact(c))" :key="k">
                <span v-if="v !== 0" class="mkt-spec">
                  <b>{{ v }}</b>
                  <i>{{ k === 'hp' ? 'HP' : STAT_META[k as 'attack'].short }}</i>
                </span>
              </template>
            </span>
            <span class="mkt-tile-foot">
              <span class="mkt-tile-price">
                <img :src="c.currency === 'PREMIUM' ? goldBarsIcon : cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                {{ c.price.toLocaleString() }}
              </span>
              <button
                class="btn-deco btn-deco--gold btn-deco--sm"
                :disabled="busy === c.id || !!rackBlocked(c) || !rackAfford(c)"
                :title="rackBlocked(c) ?? (!rackAfford(c) ? 'Not enough funds' : `Buy ${c.name}`)"
                @click.stop="onBuyRack(c)"
              >{{ busy === c.id ? '…' : (rackBlocked(c) ?? (rackAfford(c) ? 'Buy' : 'Short')) }}</button>
            </span>
          </div>
          </template>
        </SwipeDeck>
      </div>

      <!-- ═══ SUPPLIES & FAVORS ══════════════════════════════════════ -->
      <div v-else-if="tab === 'supplies'">
        <div v-for="g in GROUPS" :key="g.key" class="mb-8">
          <h2 class="font-heading text-lg text-gold">{{ g.title }}</h2>
          <p class="font-body text-xs text-text-dim mb-3">{{ g.blurb }}</p>

          <p v-if="itemsOf(g.key).length === 0" class="font-body text-text-dim text-sm">Nothing here yet.</p>
          <div v-else class="grid gap-2 sm:grid-cols-2">
            <div v-for="i in itemsOf(g.key)" :key="i.id" class="mkt-row" :class="{ 'guide-glow': props.spotlightItem === i.id }">
              <div class="flex items-center gap-3 min-w-0">
                <img v-if="shopArt(i.kind)" :src="shopArt(i.kind)" :alt="i.name" class="mkt-row-art" />
                <div class="min-w-0">
                  <div class="font-heading text-text truncate">
                    {{ i.name }}
                    <span v-if="durationLabel(i.durationMs)" class="text-text-dim text-xs font-body">· {{ durationLabel(i.durationMs) }}</span>
                    <span v-if="activeHint(i)" class="ml-1 text-[10px] uppercase tracking-wider text-success">{{ activeHint(i) }}</span>
                  </div>
                  <div class="font-body text-xs text-text-dim">{{ magnitudeLabel(i) ?? i.description }}</div>
                  <div v-if="dailyCapLabel(i)" class="font-label uppercase tracking-wider text-[10px] text-gold-bright mt-0.5">{{ dailyCapLabel(i) }}</div>
                </div>
              </div>
              <div class="shrink-0 text-right">
                <div class="font-heading flex items-center justify-end gap-1" :class="i.currency === 'PREMIUM' ? 'text-gold-bright' : 'text-gold'">
                  <img :src="i.currency === 'PREMIUM' ? goldBarsIcon : cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />{{ i.price.toLocaleString() }}
                </div>
                <button
                  :disabled="busy === i.id || !!blockedReason(i) || !affordable(i)"
                  @click="onBuySupply(i)"
                  class="btn-deco btn-deco--gold btn-deco--sm"
                  :title="blockedReason(i) ?? (!affordable(i) ? 'Not enough funds' : '')"
                >{{ busy === i.id ? '…' : (blockedReason(i) ?? (affordable(i) ? 'Buy' : 'Short')) }}</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Golden Chest — ornate gacha card -->
        <div v-if="chestItem" class="mb-8">
          <h2 class="font-heading text-lg text-gold-bright">The Golden Chest</h2>
          <p class="font-body text-xs text-text-dim mb-3">The house's mystery box — could be anything.</p>
          <div class="mkt-chest">
            <div class="mkt-chest-wash" aria-hidden="true"></div>
            <img v-if="shopArt(chestItem.kind)" :src="shopArt(chestItem.kind)" alt="" class="mkt-chest-art" />
            <div class="mkt-chest-body">
              <span class="font-heading text-gold-bright text-lg leading-tight">{{ chestItem.name }}</span>
              <span class="font-body text-sm text-text-dim leading-snug">{{ chestItem.description }}</span>
            </div>
            <div class="mkt-chest-buy">
              <span class="mkt-chest-price">
                <img :src="chestItem.currency === 'PREMIUM' ? goldBarsIcon : cashIcon" class="w-4 h-4 shrink-0" alt="" />
                {{ chestItem.price.toLocaleString() }}
              </span>
              <button
                class="btn-deco btn-deco--gold"
                :disabled="busy === chestItem.id || !!blockedReason(chestItem) || !affordable(chestItem)"
                :title="blockedReason(chestItem) ?? (!affordable(chestItem) ? 'Not enough gold bars' : 'Open the chest')"
                @click="onBuySupply(chestItem)"
              >{{ busy === chestItem.id ? '…' : (blockedReason(chestItem) ?? (affordable(chestItem) ? 'Pull' : 'Short')) }}</button>
            </div>
          </div>
        </div>

        <!-- Stash Expansion — surfaces bagCount/bagCap so the purchase visibly matters -->
        <div v-if="expansionItem">
          <h2 class="font-heading text-lg text-gold">Stash Expansion</h2>
          <div class="mkt-row">
            <div class="min-w-0">
              <div class="font-heading text-text">{{ expansionItem.name }}</div>
              <div class="font-body text-xs text-text-dim">{{ expansionItem.description }}</div>
              <div v-if="slots" class="font-label uppercase tracking-wider text-[11px] text-gold mt-1">
                {{ slots.current }} / {{ slots.max }} slots
              </div>
              <div class="font-body text-[11px] text-text-dim/70 mt-0.5">
                Flat price to 100 slots · higher price up to {{ slots?.max ?? 200 }}.
              </div>
            </div>
            <div class="shrink-0 text-right">
              <template v-if="!slotsMaxed">
                <div class="font-heading flex items-center justify-end gap-1 text-gold-bright">
                  <img :src="goldBarsIcon" class="w-3.5 h-3.5 shrink-0" alt="" />{{ expansionItem.price.toLocaleString() }}
                </div>
                <button
                  :disabled="busy === expansionItem.id || !affordable(expansionItem)"
                  @click="onBuySupply(expansionItem)"
                  class="btn-deco btn-deco--gold btn-deco--sm"
                >{{ busy === expansionItem.id ? '…' : (affordable(expansionItem) ? `+${slots?.step ?? 5} slots` : 'Short') }}</button>
              </template>
              <div v-else class="font-label uppercase tracking-wider text-[10px] text-text-dim border border-border rounded px-2.5 py-1.5" style="white-space:nowrap">Maxed · {{ slots?.max }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ SELL ═══════════════════════════════════════════════════ -->
      <div v-else class="grid gap-6 grid-cols-1 lg:grid-cols-[1fr_340px]">
        <div>
          <h2 class="font-label uppercase tracking-wider text-xs text-text-dim mb-3">Your bag ({{ bag.length }})</h2>
          <p v-if="bag.length === 0" class="font-body text-text-dim text-sm">Nothing unequipped to sell.</p>
          <SwipeDeck
            v-else :items="bag" :page-size="6"
            min-col="150px" gap="0.75rem" :item-key="(a: any) => a.id"
          >
            <template #default="{ item: a }">
            <button
              class="mkt-tile"
              :class="{ 'mkt-legendary': a.rarity === 'LEGENDARY', 'mkt-tile-selected': sellTarget?.id === a.id }"
              :style="{ '--rar': RARITY_HEX[a.rarity] }"
              @click="pickToSell(a)"
            >
              <span class="mkt-tile-lv">Lv {{ a.level }}</span>
              <img :src="itemThumb(a.type, a.rarity, a.name)" :alt="a.type" class="mkt-tile-art" width="52" height="52" loading="lazy" />
              <span class="mkt-tile-name" :style="{ color: RARITY_HEX[a.rarity] }">{{ a.name }}</span>
              <span class="mkt-tile-meta">{{ statLine(a) }}</span>
            </button>
            </template>
          </SwipeDeck>
        </div>

        <aside class="h-fit p-5 rounded border border-border bg-surface corner-frame relative">
          <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
          <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>

          <h2 class="font-label uppercase tracking-wider text-xs text-text-dim mb-1">Sell from the bag</h2>
          <p class="font-body text-text-dim text-xs mb-4">
            Equipped gear can't be sold. Take the instant cut, or fence it for more.
          </p>

          <p v-if="!sellTarget" class="font-body text-text-dim text-sm">Pick something from the bag on the left.</p>

          <div v-else>
            <div class="mkt-sell-item">
              <img :src="itemThumb(sellTarget.type, sellTarget.rarity, sellTarget.name)" class="mkt-sell-art" alt="" />
              <div class="min-w-0">
                <div class="font-heading truncate" :style="{ color: RARITY_HEX[sellTarget.rarity] }">{{ sellTarget.name }}</div>
                <div class="font-body text-xs text-text-dim">{{ rarityLabel(sellTarget.rarity) }} · Lv {{ sellTarget.level }}</div>
              </div>
            </div>

            <p v-if="quoteLoading" class="font-body text-text-dim text-sm">Fetching a quote&hellip;</p>

            <template v-else-if="sellQuote">
              <button class="mkt-sell-opt mkt-sell-opt--quick" :disabled="busy !== null" @click="onQuicksell">
                <span class="mkt-sell-opt-txt">
                  <span class="mkt-sell-opt-h">Quick-sell</span>
                  <span class="mkt-sell-opt-sub">Instant · you lose {{ 100 - sellQuote.quick.pct }}%</span>
                </span>
                <span class="mkt-sell-opt-pay"><img :src="cashIcon" class="w-4 h-4 shrink-0" alt="" />+{{ sellQuote.quick.payout.toLocaleString() }}</span>
              </button>

              <button class="mkt-sell-opt mkt-sell-opt--fence" :disabled="busy !== null" @click="onMarketSell">
                <span class="mkt-sell-opt-txt">
                  <span class="mkt-sell-opt-h">Fence it</span>
                  <span class="mkt-sell-opt-sub">{{ sellQuote.market.lockHours }}h lock · you lose {{ 100 - sellQuote.market.pct }}%</span>
                </span>
                <span class="mkt-sell-opt-pay mkt-sell-opt-pay--gold"><img :src="cashIcon" class="w-4 h-4 shrink-0" alt="" />+{{ sellQuote.market.payout.toLocaleString() }}</span>
              </button>

              <p class="font-body text-[11px] text-text-dim/70 mt-2 leading-snug">
                Quick-sell pays half, right now. The fence pays {{ sellQuote.market.pct }}% — but the cash clears only after a {{ sellQuote.market.lockHours }}-hour lock.
              </p>
            </template>
          </div>

          <!-- AT THE FENCE — locked payouts, auto-collected once they mature -->
          <div v-if="pending.length" class="mkt-fence">
            <div class="mkt-fence-h">At the fence <span class="text-text-dim">· {{ pending.length }}</span></div>
            <div v-for="p in pending" :key="p.id" class="mkt-fence-row">
              <span class="mkt-fence-name truncate" :style="{ color: RARITY_HEX[p.rarity] }">{{ p.itemName }}</span>
              <span class="mkt-fence-pay"><img :src="cashIcon" class="w-3 h-3 shrink-0" alt="" />+{{ p.payout.toLocaleString() }}</span>
              <span class="mkt-fence-time">{{ untilLabel(p.matureAt) }}</span>
            </div>
          </div>
        </aside>
      </div>
    </template>

    <!-- Rack item preview — view-only ItemModal w/ Buy action + compare-vs-equipped -->
    <ItemModal
      v-if="rackInspect"
      :artifact="cardToArtifact(rackInspect)"
      :slot="slotOf(rackInspect.type)"
      :is-equipped="false"
      :equipped-in-slot="equippedFor(rackInspect)"
      mode="catalog"
      :price-label="priceLabel(rackInspect.price, rackInspect.currency)"
      :alt-price-label="rackInspect.altBars ? priceLabel(rackInspect.altBars, 'PREMIUM') : ''"
      :buy-busy="busy === rackInspect.id"
      :blocked-reason="rackModalBlocked(rackInspect)"
      @buy="onBuyRack(rackInspect, 'SOFT')"
      @buy-alt="onBuyRack(rackInspect, 'PREMIUM')"
      @close="rackInspect = null"
    />

    <!-- Golden Chest opening cinematic -->
    <ChestReveal v-if="chestReward" :reward="chestReward" @close="chestReward = null" />

  </section>
</template>

<style scoped>
/* ── Wallet strip ─────────────────────────────────────────────────────── */
.mkt-wallet-chip {
  display: flex; align-items: center; gap: 0.4rem;
  padding: 6px 12px;
  border: 1px solid var(--color-border, #2a2320);
  border-radius: 999px;
  background: var(--color-surface, #14121a);
}
.mkt-bars-pulse { animation: legendary-pulse 2.4s ease-in-out infinite; --rar: var(--color-gold-bright, #e3c878); }

.mkt-count {
  font-size: 10px; padding: 0 6px; border-radius: 999px;
  background: rgba(0,0,0,0.35); color: var(--color-text-dim, #9a8f7a);
}
.tab-deco-active .mkt-count { color: var(--color-gold, #c8a24c); background: color-mix(in srgb, var(--color-gold, #c8a24c) 18%, transparent); }

/* ── Filter chip / subtab embellishment (Rack rarity dot) ─────────────── */
.mkt-dot { width: 7px; height: 7px; border-radius: 999px; }

/* ── Item tiles (Rack + Sell bag) ─────────────────────────────────────── */
.mkt-tile-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 0.75rem; }
.mkt-tile {
  position: relative;
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 0.8rem 0.6rem 0.65rem;
  border-radius: 10px;
  border: 1px solid var(--color-border, #2a2320);
  background: linear-gradient(180deg, color-mix(in srgb, var(--rar, #9a927f) 10%, transparent), rgba(0,0,0,0.16)), var(--color-surface, #14121a);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
  cursor: pointer; text-align: center;
  transition: transform 130ms ease, box-shadow 130ms ease;
}
.mkt-tile:hover { transform: translateY(-2px); box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 6px 16px rgba(0,0,0,0.4); }
.mkt-tile-selected { outline: 2px solid var(--color-gold, #c8a24c); outline-offset: 1px; }

/* ── Two-tier sell aside ──────────────────────────────────────────────── */
.mkt-sell-item {
  display: flex; align-items: center; gap: 10px; margin-bottom: 14px;
  padding-bottom: 12px; border-bottom: 1px solid color-mix(in srgb, var(--color-border) 60%, transparent);
}
.mkt-sell-art {
  width: 44px; height: 44px; border-radius: 8px; object-fit: cover; flex: none;
  border: 1px solid var(--color-border);
}
.mkt-sell-opt {
  display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%;
  padding: 10px 12px; margin-bottom: 8px;
  border-radius: 8px; border: 1px solid var(--color-border);
  background: rgba(0, 0, 0, 0.25);
  transition: border-color 130ms ease, background 130ms ease, transform 90ms ease;
  text-align: left;
}
.mkt-sell-opt:hover:not(:disabled) { transform: translateY(-1px); }
.mkt-sell-opt:disabled { opacity: 0.55; cursor: wait; }
.mkt-sell-opt--quick:hover:not(:disabled) { border-color: color-mix(in srgb, var(--color-error) 55%, transparent); background: rgba(179, 65, 62, 0.08); }
.mkt-sell-opt--fence { border-color: color-mix(in srgb, var(--color-gold) 40%, transparent); }
.mkt-sell-opt--fence:hover:not(:disabled) { border-color: var(--color-gold); background: rgba(200, 162, 76, 0.08); }
.mkt-sell-opt-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.mkt-sell-opt-h { font-family: var(--font-heading, serif); font-size: 15px; color: var(--color-text); }
.mkt-sell-opt-sub { font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-dim); }
.mkt-sell-opt-pay {
  display: inline-flex; align-items: center; gap: 5px; flex: none;
  font-family: var(--font-heading, serif); font-size: 17px; font-weight: 700;
  color: var(--color-text); font-variant-numeric: tabular-nums;
}
.mkt-sell-opt-pay--gold { color: var(--color-gold-bright, #e3c878); }

.mkt-fence {
  margin-top: 16px; padding-top: 12px;
  border-top: 1px solid color-mix(in srgb, var(--color-border) 60%, transparent);
}
.mkt-fence-h {
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase;
  color: var(--color-gold); margin-bottom: 8px;
}
.mkt-fence-row {
  display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center;
  padding: 6px 0; font-family: var(--font-body, serif); font-size: 13px;
  border-bottom: 1px solid color-mix(in srgb, var(--color-border) 40%, transparent);
}
.mkt-fence-row:last-child { border-bottom: 0; }
.mkt-fence-name { min-width: 0; }
.mkt-fence-pay { display: inline-flex; align-items: center; gap: 4px; color: var(--color-success); font-variant-numeric: tabular-nums; }
.mkt-fence-time {
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--color-text-dim); min-width: 52px; text-align: right;
}
.mkt-legendary { animation: legendary-pulse 2.2s ease-in-out infinite; }
.mkt-hot {
  position: absolute; top: 5px; left: 6px;
  font-family: var(--font-label, sans-serif); font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--color-bg, #0a0a0e); background: var(--color-gold-bright, #e3c878);
  border-radius: 999px; padding: 1px 6px;
}
.mkt-tile-lv {
  position: absolute; top: 5px; right: 6px;
  font-family: var(--font-label, sans-serif); font-size: 9px;
  color: var(--color-text-dim, #9a8f7a); background: rgba(0,0,0,0.5);
  border-radius: 999px; padding: 1px 6px;
}
/* art sits in a rarity-lit well — the piece under a display lamp */
.mkt-tile-artwrap {
  position: relative;
  display: grid; place-items: center;
  width: 100%; padding: 8px 0 4px;
  isolation: isolate;
}
.mkt-tile-artwrap::before {
  content: '';
  position: absolute; z-index: -1;
  width: 74px; height: 74px; border-radius: 9999px;
  background: radial-gradient(circle, color-mix(in srgb, var(--rar, #9a927f) 26%, transparent), transparent 70%);
}
.mkt-tile-art {
  width: 52px; height: 52px; object-fit: cover; border-radius: 8px;
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--rar, #9a927f) 45%, transparent),
    0 4px 12px rgba(0, 0, 0, 0.55);
  transition: transform 160ms ease;
}
.mkt-tile:hover .mkt-tile-art { transform: scale(1.07); }
.mkt-tile-name {
  max-width: 100%;
  font-family: var(--font-heading, serif); font-size: 13px; line-height: 1.25;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mkt-tile-meta {
  max-width: 100%;
  font-family: var(--font-label, monospace); font-size: 8.5px; font-weight: 600;
  color: var(--color-text-dim, #9a8f7a); text-transform: uppercase; letter-spacing: 0.14em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mkt-tile-fx { font-style: normal; color: var(--color-purple-light, #9a86ab); }

/* the spec plate: one even strip, hairline-ruled, value struck over its label */
.mkt-specs {
  display: flex; align-items: stretch; justify-content: center;
  width: calc(100% + 1.2rem);
  margin: 8px -0.6rem 0;
  margin-top: auto;
  padding: 6px 4px 5px;
  border-top: 1px solid color-mix(in srgb, var(--rar, #9a927f) 22%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--rar, #9a927f) 14%, transparent);
  background: rgba(0, 0, 0, 0.22);
}
.mkt-spec {
  flex: 1 1 0; min-width: 0;
  display: flex; flex-direction: column; align-items: center; gap: 2px;
}
.mkt-spec + .mkt-spec { border-left: 1px solid rgba(236, 227, 207, 0.07); }
.mkt-spec b {
  font-family: var(--font-heading, serif); font-weight: 700;
  font-size: 13.5px; line-height: 1;
  color: var(--color-text, #ece3cf);
  font-variant-numeric: tabular-nums;
}
.mkt-spec i {
  font-style: normal;
  font-family: var(--font-label, monospace); font-size: 7px; font-weight: 700;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: color-mix(in srgb, var(--rar, #9a927f) 65%, var(--color-text-dim));
}
.mkt-tile-price {
  display: flex; align-items: center; gap: 4px;
  margin-top: 7px;
  font-family: var(--font-heading, serif); font-size: 14px;
  color: var(--color-gold, #c8a24c);
  font-variant-numeric: tabular-nums;
}
/* price + Buy side by side at the tile's foot (the card itself opens details) */
.mkt-tile-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 6px;
  width: 100%;
  margin-top: auto;
  padding-top: 4px;
}
.mkt-tile-foot .mkt-tile-price { margin-top: 0; }

/* ── Listing / catalog rows (Supplies & Favors) ────────────────────────── */
.mkt-row {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  min-width: 0; /* grid/flex child must shrink to its track, never overflow sideways */
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--color-border, #2a2320);
  background: var(--color-surface, #14121a);
}
.mkt-row-art { width: 44px; height: 44px; border-radius: 7px; object-fit: cover; flex-shrink: 0; border: 1px solid var(--color-border, #2a2320); }

/* ── Golden Chest ornate card ─────────────────────────────────────────── */
.mkt-chest {
  position: relative; isolation: isolate; overflow: hidden;
  display: flex; align-items: center; gap: 16px; width: 100%;
  padding: 1.1rem 1.25rem;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--color-gold-bright, #e3c878) 45%, transparent);
  background: var(--color-surface, #14121a);
  box-shadow: 0 0 18px color-mix(in srgb, var(--color-gold-bright, #e3c878) 14%, transparent);
}
/* soft breathing gold aura behind the row (doesn't touch the button/text) */
.mkt-chest-wash {
  position: absolute; inset: 0; z-index: -1; pointer-events: none;
  background: linear-gradient(135deg, color-mix(in srgb, var(--color-gold, #c8a24c) 12%, transparent), transparent 62%);
  animation: legendary-pulse 2.8s ease-in-out infinite;
}
.mkt-chest-art {
  width: 60px; height: 60px; border-radius: 10px; object-fit: cover; flex: none;
  border: 1px solid color-mix(in srgb, var(--color-gold-bright, #e3c878) 40%, transparent);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
}
.mkt-chest-body { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0; }
.mkt-chest-buy { display: flex; align-items: center; gap: 16px; flex: none; }
.mkt-chest-price {
  display: flex; align-items: center; gap: 5px;
  font-family: var(--font-heading, serif);
  color: var(--color-gold-bright, #e3c878); font-size: 17px; font-weight: 700;
  font-variant-numeric: tabular-nums;
}
@media (max-width: 640px) {
  .mkt-chest { flex-wrap: wrap; }
  .mkt-chest-body { flex-basis: calc(100% - 76px); }
  .mkt-chest-buy { width: 100%; justify-content: space-between; }
}

/* ── Responsive ───────────────────────────────────────────────────────── */
@media (max-width: 1100px) {
  .mkt-tile-grid { grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); }
}

@media (prefers-reduced-motion: reduce) {
  .mkt-bars-pulse, .mkt-legendary, .mkt-chest-wash {
    animation: none !important;
  }
}
</style>
